import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import api from '../../../../services/api';

const CHANNEL_LABELS = { whatsapp: 'WhatsApp', email: 'E-mail' };

// Tela de envio em LOTE de Documentos - dada uma tag (ex: "Guia
// Prefeitura") e opcionalmente uma data de referência, mostra todo
// cliente que tem documento nessa tag e o status de envio (por canal),
// pra percorrer e mandar só o que falta sem perder o controle de quem já
// recebeu. Manda sempre pro telefone/e-mail cadastrado na própria
// Organization (aba "Dados da empresa"). Complementa a biblioteca por
// cliente (aba "Documentos" dentro da edição da Organização), que é onde
// o documento é cadastrado.
const EnvioDocumentos = () => {
    const [appId, setAppId] = useState(null);
    const [folders, setFolders] = useState([]);
    const [folderId, setFolderId] = useState('');
    const [referenceDate, setReferenceDate] = useState('');
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [whatsappIntegrations, setWhatsappIntegrations] = useState([]);
    const [emailIntegrations, setEmailIntegrations] = useState([]);
    const [sendSettings, setSendSettings] = useState({ default_whatsapp_integration: '', default_email_integration: '' });
    const [savingSettings, setSavingSettings] = useState(false);

    const [sendTarget, setSendTarget] = useState(null); // { organization, document }
    const [sendChannels, setSendChannels] = useState({ whatsapp: true, email: false });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        const init = async () => {
            try {
                const appRes = await api.get('v1/apps/default/');
                const currentAppId = appRes.data?.app?.id;
                setAppId(currentAppId);
                const [foldersRes, integrationsRes, settingsRes] = await Promise.all([
                    api.get(`v1/apps/${currentAppId}/document_folders/`),
                    api.get(`v1/apps/${currentAppId}/integrations/`),
                    api.get(`v1/apps/${currentAppId}/document_send_settings/`)
                ]);
                setFolders(foldersRes.data?.folders || []);
                const integrations = integrationsRes.data?.integrations || [];
                setWhatsappIntegrations(integrations.filter((i) => i.type === 'whatsapp'));
                setEmailIntegrations(integrations.filter((i) => i.type === 'email'));
                setSendSettings({
                    default_whatsapp_integration: settingsRes.data?.default_whatsapp_integration || '',
                    default_email_integration: settingsRes.data?.default_email_integration || ''
                });
            } catch (error) {
                console.error('Erro ao carregar tags', error);
                setStatus({ type: 'error', message: 'Erro ao carregar tags.' });
            }
        };
        init();
    }, []);

    const handleSaveSendSettings = async (field, value) => {
        const updated = { ...sendSettings, [field]: value };
        setSendSettings(updated);
        setSavingSettings(true);
        try {
            await api.patch(`v1/apps/${appId}/document_send_settings/`, { [field]: value || null });
        } catch (error) {
            console.error('Erro ao salvar canal padrão', error);
            setStatus({ type: 'error', message: 'Erro ao salvar canal padrão.' });
        } finally {
            setSavingSettings(false);
        }
    };

    const integrationLabel = (integration) => integration.displayName || integration.phoneNumber || integration.client_name || integration.id;

    const buscar = async () => {
        if (!appId || !folderId) return;
        setLoading(true);
        setStatus({ type: '', message: '' });
        try {
            const params = referenceDate ? `?reference_date=${referenceDate}` : '';
            const response = await api.get(`v1/apps/${appId}/document_folders/${folderId}/send_overview/${params}`);
            setOverview(response.data);
        } catch (error) {
            console.error('Erro ao carregar envio em lote', error);
            setStatus({ type: 'error', message: 'Erro ao carregar envio em lote.' });
        } finally {
            setLoading(false);
        }
    };

    const channelStatus = (doc, channel) => doc.shipments.some((s) => s.channel === channel && s.status === 'sent');

    const openSendModal = (organization, doc) => {
        setSendTarget({ organization, document: doc });
        setSendChannels({
            whatsapp: !!organization.phone && !channelStatus(doc, 'whatsapp'),
            email: !!organization.email && !channelStatus(doc, 'email')
        });
    };

    const handleSend = async () => {
        const channels = Object.entries(sendChannels)
            .filter(([, enabled]) => enabled)
            .map(([channel]) => channel);
        if (channels.length === 0) return;

        setSending(true);
        try {
            const response = await api.post(`v1/apps/${appId}/documents/${sendTarget.document.id}/shipments/`, { channels });
            const newShipments = response.data?.shipments || [];
            setOverview((prev) => ({
                ...prev,
                organizations: prev.organizations.map((org) =>
                    org.id !== sendTarget.organization.id
                        ? org
                        : {
                              ...org,
                              documents: org.documents.map((doc) =>
                                  doc.id !== sendTarget.document.id ? doc : { ...doc, shipments: [...doc.shipments, ...newShipments] }
                              )
                          }
                )
            }));
            setSendTarget(null);
        } catch (error) {
            console.error('Erro ao enviar documento', error);
            setStatus({ type: 'error', message: 'Erro ao enviar documento.' });
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                Envio de Documentos
            </Typography>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {(whatsappIntegrations.length > 1 || emailIntegrations.length > 1) && (
                <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        Canal padrão pra envio de Documentos
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {whatsappIntegrations.length > 1 && (
                            <TextField
                                select
                                size="small"
                                label="WhatsApp padrão"
                                value={sendSettings.default_whatsapp_integration}
                                onChange={(e) => handleSaveSendSettings('default_whatsapp_integration', e.target.value)}
                                disabled={savingSettings}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="">Nenhum escolhido (vai falhar se tiver mais de 1)</MenuItem>
                                {whatsappIntegrations.map((integration) => (
                                    <MenuItem key={integration.id} value={integration.id}>
                                        {integrationLabel(integration)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {emailIntegrations.length > 1 && (
                            <TextField
                                select
                                size="small"
                                label="E-mail padrão"
                                value={sendSettings.default_email_integration}
                                onChange={(e) => handleSaveSendSettings('default_email_integration', e.target.value)}
                                disabled={savingSettings}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="">Nenhum escolhido (vai falhar se tiver mais de 1)</MenuItem>
                                {emailIntegrations.map((integration) => (
                                    <MenuItem key={integration.id} value={integration.id}>
                                        {integrationLabel(integration)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>
                </Box>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="center">
                <TextField select size="small" label="Tag" value={folderId} onChange={(e) => setFolderId(e.target.value)} sx={{ minWidth: 220 }}>
                    {folders.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                            {f.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    type="date"
                    label="Data de referência (opcional)"
                    InputLabelProps={{ shrink: true }}
                    value={referenceDate}
                    onChange={(e) => setReferenceDate(e.target.value)}
                    sx={{ minWidth: 220 }}
                />
                <Button variant="contained" onClick={buscar} disabled={!folderId || loading}>
                    {loading ? <CircularProgress size={18} /> : 'Buscar'}
                </Button>
            </Stack>

            {overview && overview.organizations.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Nenhum cliente com documento nessa tag{referenceDate ? ' e data de referência' : ''}.
                </Typography>
            )}

            {overview &&
                overview.organizations.map((org) => (
                    <Box key={org.id} sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                            {org.name}
                        </Typography>
                        {!org.phone && !org.email && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                Sem telefone/e-mail cadastrado - não é possível enviar pra esse cliente ainda.
                            </Alert>
                        )}
                        <List dense>
                            {org.documents.map((doc) => (
                                <ListItem key={doc.id} sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={doc.name}
                                        secondary={doc.reference_date ? `Referência: ${doc.reference_date}` : null}
                                    />
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            size="small"
                                            label={`WhatsApp: ${channelStatus(doc, 'whatsapp') ? 'enviado' : 'pendente'}`}
                                            color={channelStatus(doc, 'whatsapp') ? 'success' : 'default'}
                                        />
                                        <Chip
                                            size="small"
                                            label={`E-mail: ${channelStatus(doc, 'email') ? 'enviado' : 'pendente'}`}
                                            color={channelStatus(doc, 'email') ? 'success' : 'default'}
                                        />
                                        <Button size="small" variant="outlined" disabled={!org.phone && !org.email} onClick={() => openSendModal(org, doc)}>
                                            Enviar
                                        </Button>
                                    </Stack>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                ))}

            <Dialog open={!!sendTarget} onClose={() => setSendTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    Enviar "{sendTarget?.document?.name}" - {sendTarget?.organization?.name}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                        Canal
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendChannels.whatsapp}
                                disabled={!sendTarget?.organization?.phone}
                                onChange={(e) => setSendChannels((prev) => ({ ...prev, whatsapp: e.target.checked }))}
                            />
                        }
                        label={sendTarget?.organization?.phone ? `WhatsApp (${sendTarget.organization.phone})` : 'WhatsApp (sem telefone cadastrado)'}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendChannels.email}
                                disabled={!sendTarget?.organization?.email}
                                onChange={(e) => setSendChannels((prev) => ({ ...prev, email: e.target.checked }))}
                            />
                        }
                        label={sendTarget?.organization?.email ? `E-mail (${sendTarget.organization.email})` : 'E-mail (sem e-mail cadastrado)'}
                    />

                    {sendTarget?.document?.shipments.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Histórico
                            </Typography>
                            <List dense>
                                {sendTarget.document.shipments.map((shipment, idx) => (
                                    <ListItem key={idx} sx={{ px: 0 }}>
                                        <ListItemText
                                            primary={`${CHANNEL_LABELS[shipment.channel] || shipment.channel} - ${shipment.status === 'sent' ? 'enviado' : 'falhou'}`}
                                            secondary={shipment.created_at ? new Date(shipment.created_at).toLocaleString('pt-BR') : null}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendTarget(null)}>Fechar</Button>
                    <Button variant="contained" onClick={handleSend} disabled={sending || (!sendChannels.whatsapp && !sendChannels.email)}>
                        {sending ? <CircularProgress size={18} /> : 'Enviar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnvioDocumentos;
